/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react"
import { Calendar, X, Plus, Trash2 } from "lucide-react"

interface CalendarSettingsDatesProps {
  name: string
  watch: (name: string) => any
  setValue: (name: string, value: any) => void
  label?: string
}

const CalendarSettingsDates: React.FC<CalendarSettingsDatesProps> = ({
  name,
  watch,
  setValue,
  label = "Fechas no laborables",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState("")
  const [isInitialized, setIsInitialized] = useState(false)

  // Watch form value
  const formValue = watch(name) || []

  // Initialize component with form value
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }, [isInitialized])

  // Format date from MM-DD to DD/MM for display
  const formatDateForDisplay = useCallback((dateString: string) => {
    if (!dateString) return ""

    // Handle MM-DD format (stored format)
    if (dateString.includes("-") && dateString.length === 5) {
      const [month, day] = dateString.split("-")
      return `${day.padStart(2, "0")}/${month.padStart(2, "0")}`
    }

    // Handle YYYY-MM-DD format (from date picker)
    if (dateString.includes("-") && dateString.length === 10) {
      const [, month, day] = dateString.split("-")
      return `${day}/${month}`
    }

    return dateString
  }, [])

  // Format date from YYYY-MM-DD to MM-DD for storage
  const formatDateForStorage = useCallback((dateString: string) => {
    if (!dateString) return ""
    const [, month, day] = dateString.split("-")
    return `${month}/${day}`
  }, [])

  // Add new date to the list
  const addDate = useCallback(() => {
    if (!selectedDate) return

    const formattedDate = formatDateForStorage(selectedDate)
    const currentDates = formValue || []

    // Check if date already exists
    if (currentDates.includes(formattedDate)) {
      return
    }

    // Add new date and sort
    const newDates = [...currentDates, formattedDate].sort()
    setValue(name, newDates)
    setSelectedDate("")
  }, [selectedDate, formValue, setValue, name, formatDateForStorage])

  // Remove date from the list
  const removeDate = useCallback(
    (dateToRemove: string) => {
      const currentDates = formValue || []
      const newDates = currentDates.filter((date: string) => date !== dateToRemove)
      setValue(name, newDates)
    },
    [formValue, setValue, name],
  )

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split("T")[0]
  }

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsModalOpen(false)
      }
    }

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [isModalOpen])

  return (
    <div className="w-full">
      {/* Main Input Field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>

        <div className="relative">
          <input
            type="text"
            readOnly
            value={formValue.map((date: string) => formatDateForDisplay(date)).join(", ")}
            placeholder="Selecciona fechas"
            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            onClick={() => setIsModalOpen(true)}
          />
          <Calendar className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
        </div>

        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
        >
          {formValue.length > 0 ? "Editar fechas" : "Agregar fechas"}
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsModalOpen(false)}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">{label}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Date Picker Section */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Seleccionar fecha</label>
                <div className="flex gap-3">
                  <input
                    type="date"
                    value={selectedDate}
                    min={getTodayDate()}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={addDate}
                    disabled={!selectedDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar
                  </button>
                </div>
              </div>

              {/* Selected Dates List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">Fechas seleccionadas</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {formValue.length} {formValue.length === 1 ? "fecha" : "fechas"}
                  </span>
                </div>

                {formValue.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No hay fechas seleccionadas</p>
                    <p className="text-xs text-gray-400 mt-1">Selecciona una fecha para comenzar</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {formValue.map((date: string, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
                      >
                        <span className="text-sm font-medium text-gray-700">{formatDateForDisplay(date)}</span>
                        <button
                          type="button"
                          onClick={() => removeDate(date)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-full transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalendarSettingsDates
